import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { currentUserClaims, getIdToken, isAuthenticated, signOut as cognitoSignOut } from './cognito';
import { onAccountBlocked, onAuthInvalidated } from './tokens';

const OWNER_ID = import.meta.env.VITE_OWNER_USER_ID as string | undefined;

interface AuthContextValue {
  authed: boolean | null;   // null = still checking on mount
  setAuthed: (v: boolean) => void;
  signOut: () => void;
  blockedMessage: string | null;
  userId: string | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    setAuthed(isAuthenticated());
    const unsubAuth = onAuthInvalidated(() => {
      setAuthed(false);
      setUserId(null);
    });
    const unsubBlocked = onAccountBlocked((msg) => setBlockedMessage(msg));
    return () => {
      unsubAuth();
      unsubBlocked();
    };
  }, []);

  useEffect(() => {
    if (!authed) return;
    getIdToken().then(() => {
      setUserId(currentUserClaims().sub);
    });
  }, [authed]);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setAuthed(false);
    setBlockedMessage(null);
    setUserId(null);
  }, []);

  const isAdmin = !!userId && !!OWNER_ID && userId === OWNER_ID;

  const value = useMemo<AuthContextValue>(
    () => ({ authed, setAuthed, signOut, blockedMessage, userId, isAdmin }),
    [authed, signOut, blockedMessage, userId, isAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
